import { FC, useState } from 'react';
import { twJoin } from 'tailwind-merge';

import Icon from 'molecules/Icon';

import FlexibleColumnTitle from 'components/Tables/FeatureComparisonTable/components/FlexibleColumnTitle';
import FlexibleTableCell from 'components/Tables/FeatureComparisonTable/components/FlexibleTableCell';
import {
  FlexibleComparisonColumnProps,
  FlexibleComparisonRowProps,
} from 'components/Tables/FeatureComparisonTable/config';
import { useScrollToAccordionItem } from 'components/Tables/FeatureComparisonTable/hooks/useScrollToAccordionItem';

interface FlexibleMobileTableProps {
  columns: (FlexibleComparisonColumnProps & { id: string })[];
  rows: (FlexibleComparisonRowProps & { id: string })[];
}

const FlexibleMobileTable: FC<FlexibleMobileTableProps> = ({ columns, rows }) => {
  // Same accordion as the Feature Comparison Table: one card per column, the first one open
  const [openColumn, setOpenColumn] = useState<string | null>(columns[0]?.id ?? null);
  const scrollRefs = useScrollToAccordionItem(openColumn || '', 96);

  return (
    <div className="flex w-full flex-col">
      {columns.map((column, colIdx) => {
        const isOpen = openColumn === column.id;
        const isFirst = colIdx === 0;
        const isLast = colIdx === columns.length - 1;

        return (
          <div
            key={column.id}
            ref={el => {
              scrollRefs.current[column.id] = el;
            }}
            className={twJoin(
              'relative flex w-full flex-col overflow-hidden border border-white/20 backdrop-blur-md',
              'bg-[radial-gradient(ellipse_98.08%_31.01%_at_50.00%_49.88%,_rgba(255,_255,_255,_0.03)_0%,_rgba(255,_255,_255,_0)_100%)]',
              isFirst && 'rounded-t-xl',
              isLast && 'rounded-b-xl',
              !isFirst && 'border-t-0',
            )}
          >
            <button
              type="button"
              aria-expanded={isOpen}
              onClick={() => setOpenColumn(prev => (prev === column.id ? null : column.id))}
              className="relative z-10 flex min-h-16 w-full items-center justify-between gap-4 px-6 py-3 text-left"
            >
              {column.highlighted && (
                <div className="pointer-events-none absolute inset-0 -z-10 bg-gradient-prismatic opacity-30" />
              )}
              <FlexibleColumnTitle title={column.title} showPrismaticLogo={column.showPrismaticLogo} />
              <Icon
                icon="chevron-down"
                size={24}
                className={twJoin(
                  'shrink-0 text-primary-400 transition-transform duration-200',
                  isOpen ? 'rotate-180' : 'rotate-0',
                )}
              />
            </button>

            <div
              className={twJoin(
                'w-full overflow-hidden border-t border-white/20 transition-all duration-300 ease-out',
                isOpen ? 'max-h-[2000px] opacity-100' : 'max-h-0 border-t-0 opacity-0',
              )}
            >
              {rows.map(row => (
                <div
                  key={row.id}
                  className="flex items-center justify-between gap-4 border-b border-white/20 px-6 py-5 last:border-b-0"
                >
                  <span className="w-2/5 shrink-0 leading-6 text-gray-300">{row.rowHeading}</span>
                  <div className="min-w-0 flex-1">
                    <FlexibleTableCell {...row.cells?.[colIdx]} alignEnd />
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FlexibleMobileTable;
